// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { setFilterDrawerIsOpen } from "@eisinc/App/Handlers/XNG/ProductList";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    view: state.pages.productList.view,
    // XNG-Changes
    filterDrawerIsOpen: state.xProductList.filterDrawerIsOpen,
    headerHeight: state.xHeader.headerHeight,
    isLoading: state.pages.productList.isLoading,
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
});

// XNG-Changes: add mapDispatchToProps
const mapDispatchToProps = {
    setFilterDrawerIsOpen,
    clearAllProductFilters,
};

// XNG-Changes: add mapDispatchToProps to props type
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    // XNG-Changes
    filtersDrawer?: DrawerPresentationProps;
    filterWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    applyBtn?: ButtonPresentationProps;
    cancelBtn?: ButtonPresentationProps;
    infoCardGridItem?: GridItemProps;
}

export const columnsStyles: ProductListColumnsStyles = {
    container: {
        gap: 20,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    leftColumnTableViewGridItem: {
        width: [12, 12, 0, 0, 0],
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
    },
    rightColumnTableViewGridItem: {
        width: 12,
    },
};

const styles = columnsStyles;

const ProductListColumns: FC<Props> = ({
    id,
    view,
    isLoading,
    filterDrawerIsOpen,
    headerHeight,
    setFilterDrawerIsOpen,
    clearAllProductFilters,
    isTabletScreen,
}) => {
    const leftColumnGridItemStyle = view === "Table" ? styles.leftColumnTableViewGridItem : styles.leftColumnGridItem;
    const rightColumnGridItemStyle =
        view === "Table" ? styles.rightColumnTableViewGridItem : styles.rightColumnGridItem;

    // <XNG-Changes>
    const handleCloseFilterDrawer = () => {
        setFilterDrawerIsOpen({ filterDrawerIsOpen: false });
    };
    const handleClearFilterDrawerValues = () => {
        clearAllProductFilters();
        handleCloseFilterDrawer();
    };
    const shellContext = useShellContext();
    // </XNG-Changes>

    return (
        <GridContainer {...styles.container} gap={isTabletScreen ? 0 : 22}>
            <GridItem {...leftColumnGridItemStyle} {...{ isLoading, cmsMode: shellContext.isEditing }}>
                <Zone contentId={id} zoneName="Content00" />
                <Zone contentId={id} zoneName="InfoCard" />
            </GridItem>
            <GridItem {...rightColumnGridItemStyle}>
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
            <GridItem {...styles.infoCardGridItem}>
                <Zone contentId={id} zoneName="InfoCard" />
            </GridItem>
            {/* XNG-Changes: add drawer for tablet & mobile screens */}
            <Drawer
                isOpen={filterDrawerIsOpen}
                handleClose={handleCloseFilterDrawer}
                position="left"
                draggable={false}
                contentLabel="filter drawer"
                headline={translate("Filter Results")}
                {...styles.filtersDrawer}
            >
                <StyledWrapper {...styles.filterWrapper} {...{ headerHeight }}>
                    <Zone contentId={id} zoneName="Content00" />
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            {...styles.cancelBtn}
                            onClick={handleClearFilterDrawerValues}
                            aria-label="Clear Filters"
                        >
                            {translate("Clear Filters")}
                        </Button>
                        <Button {...styles.applyBtn} onClick={handleCloseFilterDrawer} aria-label="Apply Filters">
                            {translate("Apply Filters")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Drawer>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    // XNG-Changes: add mapDispatchToProps
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        // XNG-Changes
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
